import { useEffect, useState } from 'react';
import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { Button, SimulatedDataBanner } from '~/components/ui';
import { useAuth } from '~/lib/auth';
import { canAccess, canSeeDashboard } from '~/lib/access';
import { QuickCreateButton } from '~/components/quick-create/QuickCreateButton';
import { NotificationBell } from '~/components/NotificationBell';
import { FeedbackWidget } from '~/components/FeedbackWidget';

export const Route = createFileRoute('/_app')({
  // No auth gate. The demo build is always signed in (src/lib/auth.tsx), so
  // the old redirect-to-/login guard could never fire and was removed.
  component: AppLayout,
});

// The 11 main tabs, in owner order (9/24 tab architecture sheet). Schedule folded back into
// Operations; Alert Page and Plumber Manager left the nav but still resolve by
// URL. Old /dashboards/<key> URLs redirect (see _app.dashboards.$dashboard).
const NAV: { label: string; to: string }[] = [
  { label: 'My Day', to: '/ninety' },
  { label: 'Jobs', to: '/jobs' },
  { label: 'Operations', to: '/dashboards/operations' },
  { label: 'Rebuild', to: '/dashboards/rebuild' },
  { label: 'Sales', to: '/dashboards/sales' },
  { label: 'Calls', to: '/dashboards/call-log' },
  { label: 'Collections', to: '/dashboards/collections' },
  { label: 'Warehouse', to: '/dashboards/warehouse' },
  { label: 'Marketing', to: '/dashboards/marketing' },
  { label: 'Brain', to: '/dashboards/brain' },
  { label: 'Company', to: '/dashboards/company-overview' },
];

// Settings is a BUTTON in the sidebar footer, never a main nav tab — the target
// architecture is 11 tabs + Settings (see TAB_ARCHITECTURE.md). It points at the
// existing /admin area, which keeps its URLs; Display TV is already a tile there.
const SETTINGS_TO = '/admin';

/**
 * App shell — monday-style left sidebar rail + content column.
 *   - dashboards live in the left sidebar nav
 *   - each page renders its own tab strip at the top of the content
 */
function AppLayout() {
  const auth = useAuth();
  const { location } = useRouterState();
  const [navOpen, setNavOpen] = useState(false);
  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setNavOpen(false); }, [location.pathname]);
  // While the mobile drawer is open, freeze the page behind it — otherwise iOS
  // routes drawer drags to the body and the CONTENT scrolls instead of the menu.
  // position:fixed is the only body lock iOS reliably honors; preserve and
  // restore the scroll position around it.
  useEffect(() => {
    if (!navOpen) return;
    const y = window.scrollY;
    const s = document.body.style;
    s.position = 'fixed';
    s.top = `-${y}px`;
    s.left = '0';
    s.right = '0';
    s.width = '100%';
    s.overflow = 'hidden';
    return () => {
      s.position = ''; s.top = ''; s.left = ''; s.right = ''; s.width = ''; s.overflow = '';
      window.scrollTo(0, y);
    };
  }, [navOpen]);
  const roles = (auth.profile?.staff_role_keys as string[] | null) ?? [];
  const allowed = auth.profile?.allowed_tabs as Record<string, string[]> | null;

  // §7.3 — deactivated accounts are locked out of the whole app. Check the REAL
  // profile so an admin previewing a deactivated user isn't locked out.
  if (auth.realProfile && auth.realProfile.is_active === false) {
    return (
      <div className="pdq-shell">
        <main className="pdq-content">
          <div className="px-6 py-10 max-w-lg mx-auto text-center space-y-4">
            <h1 className="font-display" style={{ fontSize: '22px', fontWeight: 700, color: '#032e5b' }}>
              Account deactivated
            </h1>
            <p className="text-sm text-[var(--color-ink-muted)]">
              Your access has been turned off. Contact an administrator if you believe this is a mistake.
            </p>
            <Button size="sm" variant="on-navy" onClick={() => auth.signOut()}>Sign out</Button>
          </div>
        </main>
      </div>
    );
  }

  const nav = NAV.filter((item) => {
    if (item.to.startsWith('/dashboards/')) {
      const dashboardKey = item.to.slice('/dashboards/'.length);
      return canSeeDashboard({ dashboardKey, to: item.to, roles, allowed });
    }
    return canAccess(item.to, roles);
  });
  return (
    <div className={'pdq-shell' + (navOpen ? ' nav-open' : '')}>
      <div className="pdq-mobile-topbar">
        <button className="pdq-mobile-burger" onClick={() => setNavOpen(true)} aria-label="Open menu">
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2}>
            <path d="M3 6h18M3 12h18M3 18h18" strokeLinecap="round" />
          </svg>
        </button>
        <OfficeFieldToggle pathname={location.pathname} />
        <div className="ml-auto"><NotificationBell /></div>
      </div>
      <div className="pdq-nav-overlay" onClick={() => setNavOpen(false)} aria-hidden />
      <aside className="pdq-sidebar">
        <Link to="/" className="pdq-brand font-display">
          <span className="pdq-brand-mark" aria-hidden />
          <span className="pdq-brand-stack">
            <img
              src="https://www.pdqfirewaterdamage.com/images/pdq-restoration-logo.jpg"
              alt="PDQ Restoration"
              style={{ width: '100%', height: 'auto', borderRadius: 8, background: '#fff', padding: '6px 8px', display: 'block' }}
            />
            <BrandClock />
          </span>
        </Link>
        <nav className="pdq-sidenav">
          {nav.map((item) => {
            const active = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
            return (
              <Link key={item.to} to={item.to}
                className={'pdq-navitem' + (active ? ' active' : '')}>
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="pdq-sidebar-foot">
          <QuickCreateButton variant="sidebar" />
          <div className="flex items-center justify-between gap-2">
            <span className="pdq-user">{auth.profile?.display_name ?? auth.user?.email}</span>
            <div className="flex items-center gap-1">
              {canAccess(SETTINGS_TO, roles) && (
                <Link to={SETTINGS_TO} className="pdq-settings-btn" title="Settings" aria-label="Settings">
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2}>
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Link>
              )}
              <NotificationBell />
            </div>
          </div>
          <Button size="sm" variant="on-navy" onClick={() => auth.signOut()}>
            Sign out
          </Button>
        </div>
      </aside>
      <main className="pdq-content">
        <SimulatedDataBanner />
        {auth.previewProfile && (
          <div className="flex flex-wrap items-center justify-center gap-3 px-4 py-2 text-sm font-semibold"
            style={{ background: '#fef3c7', color: '#92400e', borderBottom: '1px solid #fde68a' }}>
            👁 Viewing as <strong>{auth.previewProfile.display_name ?? auth.previewProfile.email}</strong> — you see exactly what they can.
            <button onClick={() => auth.setPreviewProfile(null)}
              className="rounded px-2.5 py-1 text-xs font-bold"
              style={{ background: '#92400e', color: '#fff' }}>
              Exit preview
            </button>
          </div>
        )}
        <Outlet />
      </main>
      <FeedbackWidget />
    </div>
  );
}

// Live date + time shown under the sidebar brand. Updates each minute.
function BrandClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="pdq-brand-clock" style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: '22px', fontWeight: 800, lineHeight: 1.1, color: 'rgba(255,255,255,0.95)' }}>
        {now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
      </span>
      <span style={{ fontSize: '19px', fontWeight: 700, lineHeight: 1.1, color: 'rgba(255,255,255,0.72)' }}>
        {now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
      </span>
    </span>
  );
}

// Mobile-only top-bar switch between the rep's Field view (the jobs list) and
// the Office hub. The container (.pdq-mobile-topbar) is hidden on desktop, so
// this only renders on phones.
function OfficeFieldToggle({ pathname }: { pathname: string }) {
  const field = pathname === '/jobs' || pathname.startsWith('/jobs/');
  return (
    <div className="pdq-of-toggle" role="tablist" aria-label="Office or Field view">
      <Link to="/" role="tab" aria-selected={!field} className={'pdq-of-seg' + (!field ? ' active' : '')}>
        🏢 Office
      </Link>
      <Link to="/jobs" role="tab" aria-selected={field} className={'pdq-of-seg' + (field ? ' active' : '')}>
        🔧 Field
      </Link>
    </div>
  );
}
